import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Toaster } from '@/components/ui/toast';

// Lazy-loaded pages — keep initial bundle small.
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ProjectListPage = lazy(() => import('@/pages/ProjectListPage').then((m) => ({ default: m.ProjectListPage })));
const ProjectNewPage = lazy(() => import('@/pages/ProjectNewPage').then((m) => ({ default: m.ProjectNewPage })));
const ProjectDetailPage = lazy(() => import('@/pages/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })));
const QuoteEditorPage = lazy(() => import('@/pages/QuoteEditorPage').then((m) => ({ default: m.QuoteEditorPage })));
const OrganizerListPage = lazy(() => import('@/pages/OrganizerListPage').then((m) => ({ default: m.OrganizerListPage })));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));
const PayrollPage = lazy(() => import('@/pages/PayrollPage').then((m) => ({ default: m.PayrollPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const FeedbackPage = lazy(() => import('@/pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })));
const AccessRequestsPage = lazy(() => import('@/pages/AccessRequestsPage').then((m) => ({ default: m.AccessRequestsPage })));

// LIFF subtree
const LiffApp = lazy(() => import('@/liff/LiffApp').then((m) => ({ default: m.LiffApp })));
const LiffQuotePage = lazy(() => import('@/liff/LiffQuotePage').then((m) => ({ default: m.LiffQuotePage })));
const LiffPaymentPage = lazy(() => import('@/liff/LiffPaymentPage').then((m) => ({ default: m.LiffPaymentPage })));
const LiffOrganizerBindPage = lazy(() => import('@/liff/LiffOrganizerBindPage').then((m) => ({ default: m.LiffOrganizerBindPage })));
const InteractiveReportPage = lazy(() => import('@/liff/InteractiveReportPage').then((m) => ({ default: m.InteractiveReportPage })));

/**
 * Bare `/liff` entry — LINE opens the single LIFF endpoint URL with the token in
 * the query string. Dispatch by which param is present:
 *   ?bind=...  → 整理師綁定頁 (organizer-bind)
 *   ?token=... → 報價頁 (quote)  [預設]
 */
function LiffEntryRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (params.get('bind')) return <Navigate to={`/liff/organizer-bind${search}`} replace />;
  return <Navigate to={`/liff/quote${search}`} replace />;
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-sm text-slate-500">
      載入中...
    </div>
  );
}

export default function App() {
  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* LIFF (public, no JWT) */}
          <Route path="/liff" element={<LiffApp />}>
            {/* LIFF entry endpoint carries the token in the query (?token=); the SPA
                route can also carry it in the path. Both render the same page. */}
            <Route path="quote" element={<LiffQuotePage />} />
            <Route path="quote/:token" element={<LiffQuotePage />} />
            <Route path="pay/:token" element={<LiffPaymentPage />} />
            {/* 整理師綁定：?bind= 走 query、亦支援路徑 token */}
            <Route path="organizer-bind" element={<LiffOrganizerBindPage />} />
            <Route path="organizer-bind/:token" element={<LiffOrganizerBindPage />} />
            {/* 互動測試報告（LINE 登入 + 逐步留言 + 吳文凱執行） */}
            <Route path="report" element={<InteractiveReportPage />} />
            {/* Bare /liff entry: keep the query string so ?token= survives the redirect. */}
            <Route index element={<LiffEntryRedirect />} />
          </Route>

          {/* Login */}
          <Route path="/login" element={<LoginPage />} />

          {/* Authenticated admin app */}
          <Route element={<AdminLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="/projects" element={<ProjectListPage />} />
            <Route path="/projects/new" element={<ProjectNewPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:projectId/edit" element={<QuoteEditorPage />} />
            <Route path="/organizers" element={<OrganizerListPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/payroll" element={<PayrollPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/access-requests" element={<AccessRequestsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </>
  );
}
